import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  format, subMonths, startOfMonth, endOfMonth,
  eachMonthOfInterval, isSameMonth
} from 'date-fns';
import { Skeleton } from '../components/ui/Skeleton';
import { Badge } from '../components/ui/Badge';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { cn } from '../lib/utils';

const { LuTrendingUp, LuUsers, LuClock, LuBookOpen, LuFilter, LuBarChart3, LuCalendar } = LuIcons;

const COLORS = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2', '#d97706', '#65a30d'];

const STATUS_COLORS = {
  present: '#10b981',
  absent: '#f43f5e',
  no_class: '#94a3b8',
  makeup: '#6366f1',
  extra: '#f59e0b',
};

const MONTHS_12 = eachMonthOfInterval({ start: subMonths(new Date(), 11), end: new Date() });

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [allAttendance, setAllAttendance] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [filterMode, setFilterMode] = useState('overview'); // 'overview' | 'student' | 'subject'
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const since = subMonths(new Date(), 12).toISOString();
      const [attRes, stuRes, subRes] = await Promise.all([
        supabase.from('attendance').select('*,subjects(id,name,level),students(id,full_name)').gte('timestamp', since),
        supabase.from('students').select('id,full_name').order('full_name'),
        supabase.from('subjects').select('id,name,level').order('name'),
      ]);
      setAllAttendance(attRes.data || []);
      setAllStudents(stuRes.data || []);
      setAllSubjects(subRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  // Filtered dataset based on active filter
  const filtered = useMemo(() => {
    let data = allAttendance;
    if (filterMode === 'student' && selectedStudent) data = data.filter(a => a.student_id === selectedStudent);
    if (filterMode === 'subject' && selectedSubject) data = data.filter(a => a.subject_id === selectedSubject);
    return data;
  }, [allAttendance, filterMode, selectedStudent, selectedSubject]);

  // 12-month attendance breakdown
  const monthlyBreakdown = useMemo(() => {
    return MONTHS_12.map(month => {
      const monthRecs = filtered.filter(a => isSameMonth(new Date(a.timestamp), month));
      const present = monthRecs.filter(a => a.status === 'present').length;
      const absent = monthRecs.filter(a => a.status === 'absent' || a.attendance_type === 'absent').length;
      const no_class = monthRecs.filter(a => a.attendance_type === 'no_class').length;
      const makeup = monthRecs.filter(a => a.attendance_type === 'makeup').length;
      const extra = monthRecs.filter(a => a.attendance_type === 'extra').length;
      const total = monthRecs.length;
      const rate = total > 0 ? Math.round((present / total) * 100) : 0;
      return {
        month: format(month, 'MMM yy'),
        present, absent, no_class, makeup, extra, total, rate
      };
    });
  }, [filtered]);

  // Attendance rate summary
  const summary = useMemo(() => {
    const total = filtered.length;
    const present = filtered.filter(a => a.status === 'present').length;
    const absent = filtered.filter(a => a.status === 'absent' || a.attendance_type === 'absent').length;
    const no_class = filtered.filter(a => a.attendance_type === 'no_class').length;
    const makeup = filtered.filter(a => a.attendance_type === 'makeup').length;
    const extra = filtered.filter(a => a.attendance_type === 'extra').length;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;
    return { total, present, absent, no_class, makeup, extra, rate };
  }, [filtered]);

  // Subject distribution
  const subjectDist = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const name = a.subjects?.name || 'Unknown';
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered]);

  // Top students by attendance
  const topStudents = useMemo(() => {
    const map = {};
    filtered.filter(a => a.status === 'present').forEach(a => {
      const name = a.students?.full_name || 'Unknown';
      const id = a.student_id;
      if (!map[id]) map[id] = { name, count: 0 };
      map[id].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [filtered]);

  // Student attendance rate breakdown (for student filter)
  const studentRateData = useMemo(() => {
    if (filterMode !== 'student' || !selectedStudent) return [];
    const studentRecs = allAttendance.filter(a => a.student_id === selectedStudent);
    const bySubject = {};
    studentRecs.forEach(a => {
      const name = a.subjects?.name || 'Unknown';
      if (!bySubject[name]) bySubject[name] = { name, present: 0, absent: 0, makeup: 0, total: 0 };
      bySubject[name].total++;
      if (a.status === 'present') bySubject[name].present++;
      else bySubject[name].absent++;
      if (a.attendance_type === 'makeup') bySubject[name].makeup++;
    });
    return Object.values(bySubject).map(s => ({
      ...s,
      rate: s.total > 0 ? Math.round((s.present / s.total) * 100) : 0
    }));
  }, [allAttendance, filterMode, selectedStudent]);

  if (loading) return (
    <div className="space-y-4 p-4">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      <Skeleton className="h-72 rounded-xl" />
    </div>
  );

  const filterLabel = filterMode === 'student' && selectedStudent
    ? allStudents.find(s => s.id === selectedStudent)?.full_name
    : filterMode === 'subject' && selectedSubject
      ? allSubjects.find(s => s.id === selectedSubject)?.name
      : 'All Data';

  return (
    <div className="space-y-5 pb-10 px-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Performance Insights</h1>
          <p className="text-slate-500 text-sm mt-0.5">12-month attendance analysis</p>
        </div>
        <div className="flex items-center gap-2 text-xs font-bold text-primary-600 bg-primary-50 px-3 py-1.5 rounded-lg border border-primary-100">
          <SafeIcon icon={LuCalendar} size={13} />
          Last 12 Months
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="flex items-center gap-2 shrink-0">
            <SafeIcon icon={LuFilter} size={14} className="text-slate-400" />
            <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Filter By</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'student', label: 'Learner' },
              { key: 'subject', label: 'Class' },
            ].map(m => (
              <button key={m.key} onClick={() => setFilterMode(m.key)}
                className={cn("px-3 py-1.5 rounded-lg text-[10px] font-black uppercase border transition-all",
                  filterMode === m.key ? "bg-primary-600 text-white border-primary-600" : "bg-slate-50 text-slate-400 border-slate-100 hover:border-slate-200")}>
                {m.label}
              </button>
            ))}
          </div>
          {filterMode === 'student' && (
            <select className="flex-1 bg-slate-50 p-2 rounded-lg font-bold text-xs border outline-none"
              value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)}>
              <option value="">Select Learner...</option>
              {allStudents.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          )}
          {filterMode === 'subject' && (
            <select className="flex-1 bg-slate-50 p-2 rounded-lg font-bold text-xs border outline-none"
              value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
              <option value="">Select Class...</option>
              {allSubjects.map(s => <option key={s.id} value={s.id}>{s.name} ({s.level})</option>)}
            </select>
          )}
          {filterLabel !== 'All Data' && (
            <Badge variant="primary" className="text-[9px] uppercase font-black">{filterLabel}</Badge>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Attendance Rate', value: `${summary.rate}%`, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: LuTrendingUp },
          { label: 'Total Sessions', value: summary.total, color: 'text-blue-600', bg: 'bg-blue-50', icon: LuBarChart3 },
          { label: 'Present', value: summary.present, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: LuIcons.LuCheckCircle },
          { label: 'Absent', value: summary.absent, color: 'text-rose-600', bg: 'bg-rose-50', icon: LuIcons.LuXCircle },
          { label: 'No Class', value: summary.no_class, color: 'text-slate-500', bg: 'bg-slate-100', icon: LuIcons.LuCalendarX },
          { label: 'Makeup', value: summary.makeup, color: 'text-indigo-600', bg: 'bg-indigo-50', icon: LuIcons.LuRefreshCw },
        ].map((c, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white dark:bg-slate-900 p-4 rounded-xl border shadow-sm">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${c.bg} ${c.color}`}>
              <SafeIcon icon={c.icon} size={16} />
            </div>
            <p className="text-[9px] font-bold uppercase text-slate-400 mb-0.5">{c.label}</p>
            <p className={`text-xl font-black ${c.color}`}>{c.value}</p>
          </motion.div>
        ))}
      </div>

      {/* 12-Month Attendance Breakdown */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-50 dark:border-slate-800">
          <h3 className="font-black text-sm uppercase tracking-widest dark:text-white flex items-center gap-2">
            <SafeIcon icon={LuBarChart3} className="text-primary-600" size={16} />
            12-Month Attendance Breakdown
          </h3>
          <p className="text-[9px] text-slate-400 font-bold uppercase mt-0.5">Present · Absent · No Class · Makeup</p>
        </div>
        <div className="p-5">
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyBreakdown} barSize={12} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', paddingTop: 12 }} />
                <Bar dataKey="present" name="Present" fill={STATUS_COLORS.present} radius={[3, 3, 0, 0]} />
                <Bar dataKey="absent" name="Absent" fill={STATUS_COLORS.absent} radius={[3, 3, 0, 0]} />
                <Bar dataKey="no_class" name="No Class" fill={STATUS_COLORS.no_class} radius={[3, 3, 0, 0]} />
                <Bar dataKey="makeup" name="Makeup" fill={STATUS_COLORS.makeup} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>

      {/* Attendance Rate Trend */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-50 dark:border-slate-800">
          <h3 className="font-black text-sm uppercase tracking-widest dark:text-white flex items-center gap-2">
            <SafeIcon icon={LuTrendingUp} className="text-emerald-500" size={16} />
            Attendance Rate Trend (%)
          </h3>
        </div>
        <div className="p-5">
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyBreakdown}>
                <defs>
                  <linearGradient id="rateGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={(v) => [`${v}%`, 'Attendance Rate']} contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 11 }} />
                <Area type="monotone" dataKey="rate" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#rateGrad)" dot={{ fill: '#10b981', r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>

      {/* Student-specific breakdown */}
      {filterMode === 'student' && selectedStudent && studentRateData.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-50 dark:border-slate-800">
            <h3 className="font-black text-sm uppercase tracking-widest dark:text-white flex items-center gap-2">
              <SafeIcon icon={LuUsers} className="text-indigo-500" size={16} />
              {allStudents.find(s => s.id === selectedStudent)?.full_name} — Subject Breakdown
            </h3>
          </div>
          <div className="p-5 space-y-3">
            {studentRateData.map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-4 border">
                <div className="flex justify-between items-center mb-2">
                  <p className="font-black text-xs uppercase">{s.name}</p>
                  <span className={cn("text-sm font-black", s.rate >= 80 ? "text-emerald-600" : s.rate >= 60 ? "text-amber-500" : "text-rose-600")}>
                    {s.rate}%
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
                  <div className={cn("h-2 rounded-full transition-all", s.rate >= 80 ? "bg-emerald-500" : s.rate >= 60 ? "bg-amber-400" : "bg-rose-500")}
                    style={{ width: `${s.rate}%` }} />
                </div>
                <div className="flex gap-3 text-[9px] font-bold text-slate-400 uppercase">
                  <span className="text-emerald-600">✓ {s.present} present</span>
                  <span className="text-rose-500">✗ {s.absent} absent</span>
                  <span className="text-indigo-500">↺ {s.makeup} makeup</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Subject Distribution */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-50 dark:border-slate-800">
            <h3 className="font-black text-sm uppercase tracking-widest dark:text-white flex items-center gap-2">
              <SafeIcon icon={LuBookOpen} className="text-primary-600" size={16} />
              Sessions by Subject
            </h3>
          </div>
          <div className="p-5">
            {subjectDist.length === 0 ? (
              <p className="text-center text-[10px] text-slate-300 font-bold uppercase py-8">No data</p>
            ) : (
              <div className="flex items-center gap-4">
                <div className="h-[200px] flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={subjectDist} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                        {subjectDist.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 w-36">
                  {subjectDist.slice(0, 6).map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-[9px] font-bold text-slate-600 dark:text-slate-400 truncate uppercase">{item.name}</span>
                      <span className="text-[9px] font-black text-slate-400 ml-auto">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Top Students */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-50 dark:border-slate-800 flex justify-between items-center">
            <h3 className="font-black text-sm uppercase tracking-widest dark:text-white flex items-center gap-2">
              <SafeIcon icon={LuUsers} className="text-primary-600" size={16} />
              Consistency Leaders
            </h3>
            <Badge variant="success" className="text-[8px] uppercase font-black">Top 6</Badge>
          </div>
          <div className="divide-y divide-slate-50 dark:divide-slate-800">
            {topStudents.length === 0 ? (
              <p className="text-center text-[10px] text-slate-300 font-bold uppercase py-8">No data</p>
            ) : topStudents.map((s, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <div className={cn(
                  "w-7 h-7 rounded-lg flex items-center justify-center font-black text-[10px] border",
                  i === 0 ? "bg-amber-50 text-amber-600 border-amber-200" :
                    i === 1 ? "bg-slate-100 text-slate-600 border-slate-200" :
                      i === 2 ? "bg-orange-50 text-orange-600 border-orange-200" :
                        "bg-white text-slate-400 border-slate-100"
                )}>
                  {i + 1}
                </div>
                <p className="flex-1 font-bold text-xs uppercase dark:text-white truncate">{s.name}</p>
                <div className="text-right">
                  <p className="text-base font-black text-primary-600">{s.count}</p>
                  <p className="text-[8px] uppercase font-bold text-slate-400">sessions</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Monthly Rate Table */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
        className="bg-white dark:bg-slate-900 rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-50 dark:border-slate-800">
          <h3 className="font-black text-sm uppercase tracking-widest dark:text-white flex items-center gap-2">
            <SafeIcon icon={LuCalendar} className="text-indigo-500" size={16} />
            Monthly Breakdown Table
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-slate-800 text-[9px] font-black uppercase text-slate-400 tracking-widest">
              <tr>
                <th className="px-4 py-3">Month</th>
                <th className="px-4 py-3 text-emerald-600">Present</th>
                <th className="px-4 py-3 text-rose-500">Absent</th>
                <th className="px-4 py-3 text-slate-400">No Class</th>
                <th className="px-4 py-3 text-indigo-500">Makeup</th>
                <th className="px-4 py-3 text-amber-500">Extra</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
              {monthlyBreakdown.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-2.5 font-black text-xs uppercase dark:text-white">{row.month}</td>
                  <td className="px-4 py-2.5 text-xs font-bold text-emerald-600">{row.present}</td>
                  <td className="px-4 py-2.5 text-xs font-bold text-rose-500">{row.absent}</td>
                  <td className="px-4 py-2.5 text-xs font-bold text-slate-400">{row.no_class}</td>
                  <td className="px-4 py-2.5 text-xs font-bold text-indigo-500">{row.makeup}</td>
                  <td className="px-4 py-2.5 text-xs font-bold text-amber-500">{row.extra}</td>
                  <td className="px-4 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-300">{row.total}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-slate-100 rounded-full h-1.5">
                        <div className={cn("h-1.5 rounded-full", row.rate >= 80 ? "bg-emerald-500" : row.rate >= 60 ? "bg-amber-400" : "bg-rose-500")}
                          style={{ width: `${row.rate}%` }} />
                      </div>
                      <span className={cn("text-[10px] font-black", row.rate >= 80 ? "text-emerald-600" : row.rate >= 60 ? "text-amber-500" : "text-rose-600")}>
                        {row.rate}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}